import { ExcelX2File, ExcelX2FileSheet } from "../models/excelModel/excelX2";

/** 두줄 세로 예상 시간 — 서버가 시트별로 계산한 값(수식·병합·이름정의 반영)을 선택 시트만큼 더한다. */
export interface X2EtaSummary {
  /** 선택 시트 예상 합(초). 통계가 없는 시트는 0 으로 친다 */
  seconds: number;
  /** 서버 작업 한도(초). 시트 목록에 없으면 3600 */
  maxJobSeconds: number;
  /** 통계가 없어 합에서 빠진 시트 수 */
  unknownSheets: number;
  selectedSheets: number;
  /** 예상이 큰 순으로 상위 시트(안내용) */
  heaviest: { name: string; seconds: number }[];
}

export function sumX2Eta(files: ExcelX2File[]): X2EtaSummary {
  let seconds = 0, unknown = 0, selected = 0, maxJob = 0;
  const all: { name: string; seconds: number }[] = [];
  files.forEach((f) =>
    (f.sheets || []).forEach((s: ExcelX2FileSheet) => {
      if (s.maxJobSeconds && s.maxJobSeconds > maxJob) maxJob = s.maxJobSeconds;
      if (!s.checked) return;
      selected += 1;
      if (s.estimatedSecondsX2 == null) { unknown += 1; return; }
      seconds += s.estimatedSecondsX2;
      all.push({ name: s.name, seconds: s.estimatedSecondsX2 });
    })
  );
  all.sort((a, b) => b.seconds - a.seconds);
  return { seconds, maxJobSeconds: maxJob || 3600, unknownSheets: unknown, selectedSheets: selected, heaviest: all.slice(0, 3) };
}

/** 초 → "약 30초" / "약 5분" / "약 1시간 20분" */
export function formatEtaKo(seconds: number): string {
  if (!(seconds > 0)) return "";
  if (seconds < 60) return `약 ${Math.max(5, Math.round(seconds / 5) * 5)}초`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `약 ${m}분`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `약 ${h}시간 ${r}분` : `약 ${h}시간`;
}

/** 통합문서 시트처럼 두줄 나누기 대상이 아닐 가능성이 큰 이름 */
export const NON_STATEMENT_SHEET = /일위대가|단가산출|수량산출|산출근거|산출서|자재단가|노임|기계경비|갑지|표지|목차|원가계산|집계표/;

/** "시트를 N개씩 나눠 K번" 권고 — 예상이 큰 시트부터 더해 한도의 80% 안에 드는 최대 개수(어느 묶음이든 한도 안). */
export interface X2SplitAdvice {
  /** 한 번에 실행해도 되는 시트 수. 0 이면 가장 큰 시트 하나만으로 한도를 넘음 */
  batchSize: number;
  runs: number;
  /** batchSize 가 0 일 때 그 시트 이름 */
  blockingSheet?: string;
}

export const SPLIT_BUDGET_RATIO = 0.8;

export function adviseX2Split(files: ExcelX2File[]): X2SplitAdvice | null {
  const eta = sumX2Eta(files);
  if (eta.selectedSheets < 2 || eta.seconds < eta.maxJobSeconds) return null;
  const budget = eta.maxJobSeconds * SPLIT_BUDGET_RATIO;
  const all: { name: string; seconds: number }[] = [];
  files.forEach((f) => (f.sheets || []).forEach((s) => { if (s.checked) all.push({ name: s.name, seconds: s.estimatedSecondsX2 || 0 }); }));
  all.sort((a, b) => b.seconds - a.seconds);
  if (all[0].seconds > budget) return { batchSize: 0, runs: 0, blockingSheet: all[0].name };
  let acc = 0, n = 0;
  for (const s of all) { if (acc + s.seconds > budget) break; acc += s.seconds; n += 1; }
  n = Math.max(1, Math.min(n, all.length - 1));
  return { batchSize: n, runs: Math.ceil(all.length / n) };
}

/**
 * 선택 시트를 앞에서부터 훑어 예상 합이 한도의 80% 안에 드는 만큼만 남기고 나머지는 선택 해제한다(파일 안에서 제자리 수정).
 * 반환: 남긴 수·해제한 수. 통계 없는 시트는 0초로 친다.
 */
export function keepSheetsWithinLimit(files: ExcelX2File[]): { kept: number; unchecked: number } {
  const eta = sumX2Eta(files);
  const budget = eta.maxJobSeconds * SPLIT_BUDGET_RATIO;
  let acc = 0, kept = 0, unchecked = 0;
  files.forEach((f) => {
    (f.sheets || []).forEach((s) => {
      if (!s.checked) return;
      const sec = s.estimatedSecondsX2 || 0;
      if (kept > 0 && acc + sec > budget) { s.checked = false; unchecked += 1; return; }
      acc += sec; kept += 1;
    });
    f.isEntireChecked = (f.sheets || []).length > 0 && (f.sheets || []).every((sh) => sh.checked);
  });
  return { kept, unchecked };
}
